<script setup>
/**
 * What WhatsApp charges this message as — the highest-consequence field on the screen, and the
 * one that used to be a `<select>`.
 *
 * ── WHY THE SELECT HAD TO GO ────────────────────────────────────────────────────────
 *
 * Three reasons, and only the third is about looks.
 *
 *   1. IT PRICES EVERY SEND. Utility is the cheap one, marketing is not, and Meta re-categorises
 *      a template it judges to be marketing — since April 2025, for a flagged business,
 *      instantly and without warning. The sentence explaining each option was shown AFTER she
 *      picked, under the closed select, so the one thing she needed in order to choose was the
 *      one thing choosing revealed.
 *   2. ONE OF THE THREE REBUILDS THE FORM. Picking a sign-in code takes seven fields off the
 *      screen and puts two others there — the largest state change in the product. A control
 *      that can do that must say so before it does it, and a select cannot: by the time the
 *      change event arrives the choice is already made.
 *   3. `TaskStepTypePicker` already established the shape this product uses for a choice that
 *      needs a sentence: rows, one line of prose each, opening in place. Never a modal, never a
 *      `<select>`.
 *
 * So all three consequences are on screen at once, before anything is chosen. The picker itself
 * decides nothing about the AUTHENTICATION collapse — it emits the intention and the page
 * answers with what that would cost, which is the only place that knows what has been written.
 *
 * LOCKED IS SHOWN, NOT HIDDEN. Meta refuses a category change on an approved template, so the
 * rows render disabled with the reason rather than collapsing to a single word: an owner
 * looking for "why can I not change this" must find the control and its answer in one place.
 */
import { useWasloI18n } from '../i18n.js';
import { CATEGORIES } from '../helpers/template-draft.js';

defineProps({
  modelValue: { type: String, default: 'UTILITY' },
  disabled: { type: Boolean, default: false },
  /** One sentence under the rows: the lock reason, or a refusal from the server. */
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error'].includes(value),
  },
  /**
   * The category a draft from a goal suggested, and why (P-4.5). The rows already show it
   * chosen; this line says it was suggested, so she knows to check it. `null` otherwise.
   */
  suggestion: { type: Object, default: null },
});

const emit = defineEmits(['select']);

const { t } = useWasloI18n();
</script>

<template>
  <div class="flex flex-col gap-2 min-w-0" data-waslo-template-category>
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t('WASLO.TEMPLATES.EDITOR.ABOUT.CATEGORY_LABEL') }}
    </p>
    <div
      class="flex flex-col gap-2"
      role="radiogroup"
      :aria-label="t('WASLO.TEMPLATES.EDITOR.ABOUT.CATEGORY_LABEL')"
    >
      <button
        v-for="category in CATEGORIES"
        :key="category"
        type="button"
        role="radio"
        :aria-checked="modelValue === category"
        :disabled="disabled"
        class="flex flex-col items-start gap-0.5 px-4 py-2.5 text-start outline outline-1 -outline-offset-1 rounded-xl disabled:cursor-not-allowed disabled:opacity-60"
        :class="
          modelValue === category
            ? 'outline-n-brand bg-n-alpha-2'
            : 'outline-n-weak hover:bg-n-alpha-1'
        "
        :data-waslo-template-category-option="category"
        @click="emit('select', category)"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t(`WASLO.TEMPLATES.CATEGORY.${category}`) }}
        </span>
        <span class="text-sm leading-snug max-w-[62ch] text-n-slate-11">
          {{ t(`WASLO.TEMPLATES.CATEGORY_NOTE.${category}`) }}
        </span>
      </button>
    </div>
    <p
      v-if="suggestion"
      class="min-w-0 mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
      dir="auto"
      data-waslo-template-category-reason
    >
      {{
        t('WASLO.TEMPLATES.EDITOR.GOAL.SUGGESTED', {
          category: t(`WASLO.TEMPLATES.CATEGORY.${suggestion.category}`),
          reason: suggestion.reason,
        })
      }}
    </p>
    <p
      v-if="message"
      class="min-w-0 mb-0 text-sm"
      :class="messageType === 'error' ? 'text-n-ruby-9' : 'text-n-slate-11'"
      data-waslo-template-category-message
    >
      {{ message }}
    </p>
  </div>
</template>
